import { useEffect, useRef } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { signupSchema, type SignupFormValues } from './schema';
import type { SignupDraft } from './types';

const DRAFT_STORAGE_KEY = 'kh-match:signup-draft';
const SAVE_DEBOUNCE_MS = 500;

const SENSITIVE_FIELDS: ReadonlyArray<keyof SignupFormValues> = ['senha'];

const DEFAULT_VALUES: Partial<SignupFormValues> = {
  nome: '',
  email: '',
  senha: '',
  cargo: '',
  empresa: '',
  segmento: '',
  estado: '',
  gargalo: '',
  tamanhoTime: undefined as unknown as number,
  tagsBuscaIds: [],
  tagsOfertaIds: [],
  consentSponsor: false,
};

const readDraft = (): SignupDraft | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SignupDraft;
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeDraft = (draft: SignupDraft) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // quota/private mode — silencioso
  }
};

export const clearSignupDraft = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const stripSensitive = (values: Partial<SignupFormValues>): Partial<SignupFormValues> => {
  const clone: Partial<SignupFormValues> = { ...values };
  for (const key of SENSITIVE_FIELDS) {
    delete clone[key];
  }
  return clone;
};

export type UseSignupFormResult = {
  methods: UseFormReturn<SignupFormValues>;
  initialStepIndex: number;
  initialAccountCreated: boolean;
  persist: (stepIndex: number, accountCreated: boolean) => void;
};

export const useSignupForm = (): UseSignupFormResult => {
  const draftRef = useRef<SignupDraft | null>(null);
  if (draftRef.current === null) {
    draftRef.current = readDraft();
  }
  const initialDraft = draftRef.current;

  const methods = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    // `onTouched`: nao valida enquanto o usuario digita pela primeira vez
    // (UX limpa, sem erro prematuro), mas qualquer mudanca subsequente
    // num campo ja tocado dispara revalidacao -- libera o botao Continuar
    // assim que o erro for corrigido sem precisar clicar de novo.
    mode: 'onTouched',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    defaultValues: {
      ...DEFAULT_VALUES,
      ...(initialDraft?.values ?? {}),
    } as SignupFormValues,
  });

  const stepIndexRef = useRef<number>(initialDraft?.currentStepIndex ?? 0);
  const accountCreatedRef = useRef<boolean>(initialDraft?.accountCreated ?? false);
  const saveTimerRef = useRef<number | null>(null);

  const persist = (stepIndex: number, accountCreated: boolean) => {
    stepIndexRef.current = stepIndex;
    accountCreatedRef.current = accountCreated;
    const draft: SignupDraft = {
      values: stripSensitive(methods.getValues()),
      currentStepIndex: stepIndex,
      accountCreated,
    };
    writeDraft(draft);
  };

  useEffect(() => {
    const subscription = methods.watch(() => {
      if (saveTimerRef.current !== null) {
        window.clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = window.setTimeout(() => {
        const draft: SignupDraft = {
          values: stripSensitive(methods.getValues()),
          currentStepIndex: stepIndexRef.current,
          accountCreated: accountCreatedRef.current,
        };
        writeDraft(draft);
      }, SAVE_DEBOUNCE_MS);
    });

    return () => {
      subscription.unsubscribe();
      if (saveTimerRef.current !== null) {
        window.clearTimeout(saveTimerRef.current);
      }
    };
  }, [methods]);

  return {
    methods,
    initialStepIndex: initialDraft?.currentStepIndex ?? 0,
    initialAccountCreated: initialDraft?.accountCreated ?? false,
    persist,
  };
};
